import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import {
  Loader2, CalendarOff, UserRoundX, Repeat, Check, X, Inbox, RefreshCw, Undo2,
} from 'lucide-react';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';
import { fmtDate, fmtTime } from '../../../lib/scheduleFormat';

const KIND_META = {
  time_off: { icon: CalendarOff, label: 'Time off', tint: 'text-violet-600' },
  drop: { icon: UserRoundX, label: 'Cannot work', tint: 'text-rose-600' },
  swap: { icon: Repeat, label: 'Swap', tint: 'text-sky-600' },
};

const STATUS_STYLES = {
  pending: { label: 'Needs your decision', cls: 'border-amber-300 bg-amber-50 text-amber-800' },
  awaiting_colleague: { label: 'Waiting on the colleague', cls: 'border-sky-300 bg-sky-50 text-sky-800' },
  approved: { label: 'Approved', cls: 'border-emerald-300 bg-emerald-50 text-emerald-800' },
  declined: { label: 'Declined', cls: 'border-rose-300 bg-rose-50 text-rose-800' },
  withdrawn: { label: 'Withdrawn', cls: 'border-slate-300 bg-slate-100 text-slate-700' },
  revoked: { label: 'Cancelled', cls: 'border-slate-300 bg-slate-100 text-slate-700' },
};

const FILTERS = [
  { key: 'open', label: 'Open' },
  { key: 'all', label: 'Everything' },
  { key: 'approved', label: 'Approved' },
  { key: 'declined', label: 'Declined' },
];

const shiftLabel = (shift) =>
  shift ? `${fmtDate(shift.date)} · ${fmtTime(shift.start_time)}–${fmtTime(shift.end_time)}` : '—';

// What approving actually does to the rota, spelled out before the click.
const outcomeCopy = (request) => {
  if (request.kind === 'swap') {
    return `${request.target_user_name} takes this shift and is asked to confirm it. ${request.user_name} comes off it.`;
  }
  if (request.kind === 'drop') {
    return `The shift stays on the rota but becomes an open shift for you to refill. Coverage will show amber until somebody takes it.`;
  }
  return `Every shift ${request.user_name} holds between ${fmtDate(request.start_date)} and ${fmtDate(request.end_date)} becomes an open shift, and they cannot be scheduled on those days until the time off is removed.`;
};

const DecisionDialog = ({ request, approve, onClose, onDone }) => {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  if (!request) return null;

  const submit = async () => {
    setSaving(true);
    try {
      const result = await adminService.decideShiftRequest(request.id, approve, note);
      toast.success(
        approve
          ? `Approved — ${result.applied?.detail || 'the rota has been updated'}`
          : 'Declined — they have been told',
      );
      onClose();
      await onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not record that decision');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="decision-dialog">
        <DialogHeader>
          <DialogTitle>
            {approve ? 'Approve' : 'Decline'} {request.user_name}&apos;s request
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm">
            <p className="font-medium text-gray-900">
              {request.kind === 'time_off'
                ? (request.start_date === request.end_date
                  ? fmtDate(request.start_date)
                  : `${fmtDate(request.start_date)} – ${fmtDate(request.end_date)}`)
                : shiftLabel(request.shift)}
            </p>
            <p className="mt-0.5 text-gray-600">
              {KIND_META[request.kind]?.label}
              {request.kind === 'swap' ? ` → ${request.target_user_name}` : ''}
            </p>
            {request.reason && (
              <p className="mt-1 text-gray-700">&ldquo;{request.reason}&rdquo;</p>
            )}
          </div>
          {approve && (
            <p className="text-sm text-gray-700" data-testid="decision-outcome">
              {outcomeCopy(request)}
            </p>
          )}
          <div>
            <p className="text-xs text-gray-500">
              Note back to {request.user_name} {approve ? '(optional)' : '— worth giving a reason'}
            </p>
            <Textarea
              className="mt-1" rows={3} value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={approve ? 'Enjoy the break' : 'We are too short that week, sorry'}
              data-testid="decision-note"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="decision-cancel">Cancel</Button>
          <Button
            className={approve ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}
            onClick={submit} disabled={saving}
            data-testid="decision-confirm"
          >
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {approve ? 'Approve' : 'Decline'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Requests = () => {
  const [filter, setFilter] = useState('open');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [decision, setDecision] = useState(null);
  const [revoking, setRevoking] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await adminService.getShiftRequests(filter));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load the request queue');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const revoke = async (request) => {
    setRevoking(request.id);
    try {
      await adminService.revokeTimeOff(request.id);
      toast.success(`${request.user_name} can be scheduled on those days again`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel that time off');
    } finally {
      setRevoking(null);
    }
  };

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => data?.requests || [], [data]);

  return (
    <div className="space-y-5" data-testid="admin-requests">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Change requests</h1>
          <p className="mt-1 text-sm text-gray-600">
            Time off, dropped shifts and swaps raised by the team. Nothing here has changed the rota
            yet — approving is what moves it.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="requests-refresh">
          <RefreshCw className={`mr-1 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((option) => (
          <Button
            key={option.key}
            size="sm"
            variant={filter === option.key ? 'default' : 'outline'}
            className={filter === option.key ? 'bg-amber-600 hover:bg-amber-700' : ''}
            onClick={() => setFilter(option.key)}
            data-testid={`requests-filter-${option.key}`}
          >
            {option.label}
            {option.key === 'open' && data?.pending ? ` (${data.pending})` : ''}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="flex min-h-[40vh] items-center justify-center" data-testid="admin-requests-loading">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center" data-testid="admin-requests-empty">
            <Inbox className="mx-auto h-10 w-10 text-gray-300" />
            <p className="mt-3 font-medium text-gray-900">Nothing to decide.</p>
            <p className="mt-1 text-sm text-gray-600">
              Requests from the team land here the moment they raise them.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {rows.map((request) => {
            const meta = KIND_META[request.kind] || KIND_META.drop;
            const Icon = meta.icon;
            const status = STATUS_STYLES[request.status] || STATUS_STYLES.pending;
            const decidable = request.status === 'pending';
            return (
              <Card
                key={request.id}
                className={decidable ? 'border-l-4 border-l-amber-500' : ''}
                data-testid={`admin-request-${request.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Icon className={`h-4 w-4 ${meta.tint}`} />
                        {request.user_name}
                        <span className="font-normal text-gray-500">— {meta.label.toLowerCase()}</span>
                      </CardTitle>
                      <p className="mt-1 text-sm text-gray-700">
                        {request.kind === 'time_off'
                          ? (request.start_date === request.end_date
                            ? fmtDate(request.start_date)
                            : `${fmtDate(request.start_date)} – ${fmtDate(request.end_date)}`)
                          : shiftLabel(request.shift)}
                        {request.kind === 'swap' && (
                          <span className="text-sky-700"> → {request.target_user_name}</span>
                        )}
                      </p>
                    </div>
                    <Badge variant="outline" className={status.cls}
                      data-testid={`admin-request-status-${request.id}`}>
                      {status.label}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 pt-0">
                  {request.reason && (
                    <p className="text-sm text-gray-700">&ldquo;{request.reason}&rdquo;</p>
                  )}
                  {request.kind === 'swap' && request.target_response === 'accepted' && (
                    <p className="text-xs text-emerald-700">
                      {request.target_user_name} has agreed to take it.
                    </p>
                  )}
                  {request.status === 'awaiting_colleague' && (
                    <p className="text-xs text-gray-500">
                      You cannot approve this until {request.target_user_name} answers.
                    </p>
                  )}
                  {request.decision && (
                    <p className="text-xs text-gray-600" data-testid={`admin-request-decision-${request.id}`}>
                      {request.decision.outcome === 'approved' ? 'Approved' : 'Declined'} by{' '}
                      {request.decision.by || 'the office'}
                      {request.affected_shift_count > 0
                        ? ` · ${request.affected_shift_count} shift${request.affected_shift_count === 1 ? '' : 's'} affected`
                        : ''}
                      {request.decision.note ? ` · “${request.decision.note}”` : ''}
                    </p>
                  )}
                  {decidable && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button
                        size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => setDecision({ request, approve: true })}
                        data-testid={`admin-approve-${request.id}`}
                      >
                        <Check className="mr-1 h-4 w-4" /> Approve
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => setDecision({ request, approve: false })}
                        data-testid={`admin-decline-${request.id}`}
                      >
                        <X className="mr-1 h-4 w-4" /> Decline
                      </Button>
                    </div>
                  )}
                  {request.kind === 'time_off' && request.status === 'approved' && (
                    <div className="pt-1">
                      <Button
                        size="sm" variant="outline"
                        disabled={revoking === request.id}
                        onClick={() => revoke(request)}
                        data-testid={`admin-revoke-${request.id}`}
                      >
                        {revoking === request.id
                          ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                          : <Undo2 className="mr-1 h-4 w-4" />}
                        Cancel this time off
                      </Button>
                      <p className="mt-1 text-xs text-gray-500">
                        Frees {request.user_name} to be scheduled again. Shifts already opened stay
                        open — reassign them yourself.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {decision && (
        <DecisionDialog
          request={decision.request}
          approve={decision.approve}
          onClose={() => setDecision(null)}
          onDone={load}
        />
      )}
    </div>
  );
};

export default Requests;
